import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppSendPush - the open chat's `/live` long poll carries the verdict of a send made from this page (2026-10-22).
// HABITAT-FREE: the service (`LiveRules`, `LiveRun`, `SendRun.LiveStates`) and the page (`AppLiveFeed`, `AppMain`)
// both read the wire through this file.
//
// ⭐ THE WIRE, ON A CHAT `/live` ASK ONLY: the page sends `sending: {"<request id>": "<progress it shows>"}` for its
// own sends that have no verdict yet ("" = queued, "translating", "sending"; at most `MaxIds`, ids in the exact shape
// `AppOutbox.NewId` mints - `IdPattern`). The service answers `sends: {"<id>": <entry>}` for the ids whose state
// MOVED: an entry is the SAME object `/send`'s check answers (`SendRules.StatusFrom`), so the page reads it with the
// one verdict rule (`AppSignUpApi.SendVerdictOf`); a receipt-less one is `{id, waiting: true, progress}`.
// ⛔ An older page sends no `sending`: never answered early, never gets the field (the `ticks` rule).
// ⛔ THE /send CHECK POLL STAYS AS THE FALLBACK; `Settle` makes the first final verdict the only one painted.

export class AppSendPush {
    /** The request id shape - `AppOutbox.NewId`'s ("s-", fourteen digits, "-", hex); `SendRules.IdOk` reads it too. */
    public static readonly IdPattern: RegExp = new RegExp("^s-[0-9]{14}-[0-9a-f]{1,16}$");
    public static readonly MaxIds: number = 10;
    public static readonly Translating: string = "translating";
    public static readonly Sending: string = "sending";
    /** The bubble's words while a translated send is being translated (the ellipsis character, never three dots). */
    public static readonly TranslatingWord: string = "Translating…";

    /** A progress word this build knows, else "". DOCUMENTED EXCEPTION: pure predicate. */
    public static ProgressOf = (Value: unknown): string => {
        if (Value === AppSendPush.Translating || Value === AppSendPush.Sending) { return Value as string; }
        return "";
    };

    /** The page's `sending`, or null (= not asked). ⛔ Bounded, ids by the allow-list, values by `ProgressOf`. */
    public static SendingIn = (Raw: unknown): Record<string, string> | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            const Out: Record<string, string> = {};
            let Count: number = 0;
            for (const Id of Object.keys(Raw as Record<string, unknown>)) {
                if (Count >= AppSendPush.MaxIds) { break; }
                if (AppSendPush.IdPattern.test(Id) === false) { continue; }
                Out[Id] = AppSendPush.ProgressOf((Raw as Record<string, unknown>)[Id]);
                Count = Count + 1;
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendPush.SendingIn");
            return null;
        }
    };

    /** Whether an entry is a verdict (anything but `waiting`). DOCUMENTED EXCEPTION: pure. */
    public static IsFinal = (Entry: Record<string, unknown>): boolean => {
        return Entry.waiting !== true;
    };

    /**
     * The entries to answer: every verdict, and every waiting one whose progress is not what the page shows.
     * DOCUMENTED EXCEPTION: pure.
     */
    public static Moved = (Shown: Record<string, string>, Known: Record<string, Record<string, unknown>>): Record<string, Record<string, unknown>> => {
        const Out: Record<string, Record<string, unknown>> = {};
        for (const Id of Object.keys(Shown)) {
            const K: Record<string, unknown> | undefined = Known[Id];
            if (K === undefined) { continue; }
            if (AppSendPush.IsFinal(K) === true || AppSendPush.ProgressOf(K.progress) !== Shown[Id]) { Out[Id] = K; }
        }
        return Out;
    };

    /** The page's ask with `sending` added (none watched = the body unchanged). DOCUMENTED EXCEPTION: JSON edit. */
    public static WithSending = (Body: string, Shown: Record<string, string>): string => {
        if (getCoreUtils().KErrorMode === true) { return Body; }
        try {
            if (Object.keys(Shown).length === 0 || nz(Body).length === 0) { return Body; }
            const Doc: Record<string, unknown> = JSON.parse(Body) as Record<string, unknown>;
            Doc.sending = Shown;
            return JSON.stringify(Doc);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendPush.WithSending");
            return Body;
        }
    };

    /** An answer's `sends` - only entries whose key passes the id shape and whose value is an object. */
    public static SendsIn = (Text: string): Record<string, Record<string, unknown>> => {
        const Out: Record<string, Record<string, unknown>> = {};
        try {
            const Doc: unknown = JSON.parse(nz(Text));
            if (Doc === null || typeof Doc !== "object") { return Out; }
            const Raw: unknown = (Doc as Record<string, unknown>).sends;
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return Out; }
            for (const Id of Object.keys(Raw as Record<string, unknown>).slice(0, AppSendPush.MaxIds)) {
                const V: unknown = (Raw as Record<string, unknown>)[Id];
                if (AppSendPush.IdPattern.test(Id) === false || V === null || typeof V !== "object" || Array.isArray(V) === true) { continue; }
                Out[Id] = V as Record<string, unknown>;
            }
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an unreadable answer from the wire is fate 1 - nothing pushed, the poll goes on.
            return Out;
        }
    };

    // ── The page's book of its own sends without a verdict ─────────────────────────────────

    private Shown: Record<string, string> = {};
    private Done: Record<string, boolean> = {};
    private DoneOrder: string[] = [];

    /** A send was queued from this page: watch it, showing `Progress`. */
    public Watch = (Id: string, Progress: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppSendPush.IdPattern.test(nz(Id)) === false || this.Done[Id] === true) { return; }
            this.Shown[Id] = AppSendPush.ProgressOf(Progress);
            const Ids: string[] = Object.keys(this.Shown);
            if (Ids.length > AppSendPush.MaxIds) { delete this.Shown[Ids[0]]; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendPush.Watch");
        }
    };

    /** The progress now shown for `Id` (it was painted). DOCUMENTED EXCEPTION: a field write. */
    public Showing = (Id: string, Progress: string): void => {
        if (this.Shown[Id] !== undefined) { this.Shown[Id] = AppSendPush.ProgressOf(Progress); }
    };

    /** What the next chat ask carries. DOCUMENTED EXCEPTION: a copy. */
    public Watched = (): Record<string, string> => {
        const Out: Record<string, string> = {};
        for (const Id of Object.keys(this.Shown)) { Out[Id] = this.Shown[Id]; }
        return Out;
    };

    /**
     * ⛔ THE ONE VERDICT: true only the FIRST time `Id` is settled - by the push or by the `/send` check poll, whichever
     * comes first; the other is then ignored, so a bubble is never painted twice (a flag added twice, a line said twice).
     */
    public Settle = (Id: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            delete this.Shown[nz(Id)];
            if (this.Done[nz(Id)] === true) { return false; }
            this.Done[nz(Id)] = true;
            this.DoneOrder.push(nz(Id));
            if (this.DoneOrder.length > 200) { delete this.Done[this.DoneOrder.shift() as string]; }
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendPush.Settle");
            return false;
        }
    };

    /** ⚠ LAST MEMBER - a static singleton is built after every static it reads (the `AssistWarm` rule). */
    public static readonly Shared: AppSendPush = new AppSendPush();
}
